import { getRouteApi, Link, useRouterState } from "@tanstack/react-router";
import { Button } from "@workspace/web-ui/components/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@workspace/web-ui/components/dropdown-menu";
import { Sheet, SheetContent } from "@workspace/web-ui/components/sheet";
import { cn } from "@workspace/web-ui/lib/utils";
import { AlignRight, BellIcon, LogOutIcon, UserIcon, ChevronRight, ChevronsUpDown } from "lucide-react";
import { useState, useMemo, useCallback, memo } from "react";
import { LogoWhite } from "~/components/logo";
import { NavigationItem, weightLossNavigationItems, urgentCareNavigationItems, platformSwitchItems } from "~/features/dashboard/data/navigation";
import { useAuth } from "~/providers/auth.provider";

type DashboardHeader = {
  items: NavigationItem[];
};

// Move outside component - no need to recreate on every render
function getActiveItem(items: NavigationItem[], pathname: string, program?: string): string | null {
  let bestMatch: { url: string, length: number } | null = null;
  for (const item of items) {
    let matchUrls: string[] = [];
    if (typeof item.matchUrls === "function") {
      matchUrls = item.matchUrls(program || "");
    } else if (Array.isArray(item.matchUrls)) {
      matchUrls = item.matchUrls;
    }
    for (const matchUrl of matchUrls) {
      if (pathname.startsWith(matchUrl)) {
        if (!bestMatch || matchUrl.length > bestMatch.length) {
          bestMatch = { url: item.dynamicUrl ? item.dynamicUrl(program || "") : item.url, length: matchUrl.length };
        }
      }
    }
  }
  if (!bestMatch && pathname === `/dashboard/${program}`) {
    const rootItem = items.find(item => (item.dynamicUrl ? item.dynamicUrl(program || "") : item.url) === `/dashboard/${program}`);
    return rootItem ? (rootItem.dynamicUrl ? rootItem.dynamicUrl(program || "") : rootItem.url) : null;
  }
  return bestMatch ? bestMatch.url : null;
}

// Memoized sub-components
const PlatformSwitcher = memo(function PlatformSwitcher({ 
  currentTitle, 
  currentInitials, 
  user, 
  isTopDropdownOpen, 
  setIsTopDropdownOpen,
  pathname,
  isUrgentCarePlatform,
  handleLogout 
}: any) {
  const platformItems = useMemo(() => {
    return platformSwitchItems?.map((item) => {
      const isActive = pathname.startsWith(item.url);
      const pathParts = pathname.split("/");
      let newPath = pathname;
      
      if (pathParts[1] === "dashboard" && pathParts.length > 2) {
        pathParts[2] = item.platform;
        newPath = pathParts.join("/");
      } else {
        newPath = item.url;
      }
      
      return { ...item, isActive, newPath };
    });
  }, [pathname]);

  return (
    <DropdownMenu onOpenChange={setIsTopDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "flex w-full items-center rounded-[12px] justify-start gap-2 py-6 px-2 cursor-pointer hover:bg-[#1E293B]",
            isTopDropdownOpen && "border-2 border-[#4272DD]"
          )}
        >
          <div className="flex h-8 w-8 items-center justify-center border-[1.7px] border-[#4272DD] rounded-md bg-[#FFFFFF1A] shadow-inner shadow-[#FFFFFF26] text-white">
            <span className="text-xs font-bold">{currentInitials}</span>
          </div>
          <div className="flex flex-col text-start">
            <span className="text-sm font-medium text-white">{currentTitle}</span>
            {user && (<span className="text-xs text-[#E9E9E9]">{user?.first_name} {user?.last_name}</span>)}
          </div>
          <ChevronsUpDown className="ml-auto h-6 w-6 text-[#E9E9E9]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={cn("w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-[12px] bg-[#1C2E47] border border-[#E9E9E9]/50 shadow-lg p-0 gap-0", isUrgentCarePlatform && "bg-[#2d0e03] border-[#2F0F04]")}>
        <div className="flex flex-col divide-y divide-[#FFFFFF1A]">
          {platformItems?.map((item) => (
            <Link
              key={item.url}
              to={item.newPath}
              className={`flex w-full items-center py-[14px] px-3 justify-between bg-transparent text-white hover:bg-[#FFFFFF0D] ${item.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={item.disabled}
            >
              <div className="flex flex-col gap-2 w-full">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{!item.isActive && "Switch to "}{item.title}</span>
                  {!item.isActive && <ChevronRight size={16} />}
                </div>
                {item.isActive && <p className="text-xs text-white opacity-[50%]">Currently active here</p>}
              </div>
            </Link>
          ))}
        </div>
        <DropdownMenuSeparator className="bg-[#E9E9E9]/50 h-[1px]" />
        <button onClick={handleLogout} className="flex w-full py-[14px] px-3 items-center justify-between bg-transparent text-[#E9E9E9] hover:bg-[#FFFFFF0D]">
          <div className="flex items-center gap-2">
            <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.00003 2.0013C4.78003 2.0013 2.84075 3.20664 1.80232 5.00179C1.61796 5.32049 1.21014 5.4294 0.891432 5.24504C0.572725 5.06068 0.463816 4.65286 0.648179 4.33415C1.91514 2.14395 4.28483 0.667969 7.00003 0.667969C11.0501 0.667969 14.3334 3.95121 14.3334 8.0013C14.3334 12.0514 11.0501 15.3346 7.00003 15.3346C4.28483 15.3346 1.91514 13.8586 0.648179 11.6685C0.463816 11.3497 0.572725 10.9419 0.891432 10.7576C1.21014 10.5732 1.61796 10.6821 1.80232 11.0008C2.84075 12.796 4.78003 14.0013 7.00003 14.0013C10.3137 14.0013 13 11.315 13 8.0013C13 4.68759 10.3137 2.0013 7.00003 2.0013Z" fill="#C85858" />
              <path d="M6.52864 4.86323C6.78899 4.60288 7.2111 4.60288 7.47145 4.86323L10.1381 7.5299C10.3985 7.79025 10.3985 8.21236 10.1381 8.47271L7.47145 11.1394C7.2111 11.3997 6.78899 11.3997 6.52864 11.1394C6.26829 10.879 6.26829 10.4569 6.52864 10.1966L8.05723 8.66797H1.00004C0.631851 8.66797 0.333374 8.36949 0.333374 8.0013C0.333374 7.63311 0.631851 7.33463 1.00004 7.33463H8.05723L6.52864 5.80604C6.26829 5.54569 6.26829 5.12358 6.52864 4.86323Z" fill="#C85858" />
            </svg>
            <span className="text-sm font-medium text-white">Log out</span>
          </div>
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

const DesktopNav = memo(function DesktopNav({ currentItems, activeItemUrl, program }: any) {
  return (
    <nav className="hidden md:flex items-center gap-6">
      {currentItems.map((item: NavigationItem) => {
        const itemUrl = item.dynamicUrl ? item.dynamicUrl(program) : item.url;
        const isActive = itemUrl === activeItemUrl;
        return (
          <Link
            key={item.title}
            to={itemUrl}
            className={cn(
              "inline-flex items-center text-sm",
              isActive ? "text-white" : "text-[#E9E9E9]/60"
            )}
          >
            {isActive && (
              <div className="mr-2 size-2 rounded-full bg-[#E9E9E9]" />
            )}
            {item.title}
          </Link>
        );
      })}
    </nav>
  );
});

export function DashboardHeader() {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isTopDropdownOpen, setIsTopDropdownOpen] = useState(false);
  
  // Optimize: Only select pathname from router state
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
  const { program } = routeApi.useParams();

  // Memoize platform detection
  const platformInfo = useMemo(() => {
    const isWeightLoss = pathname.startsWith("/dashboard/weight-loss");
    const isUrgentCare = pathname.startsWith("/dashboard/urgent-care");
    
    return {
      isWeightLoss,
      isUrgentCare,
      currentItems: isWeightLoss ? weightLossNavigationItems : isUrgentCare ? urgentCareNavigationItems : [],
      currentTitle: isWeightLoss ? "Weight Loss" : isUrgentCare ? "Urgent Care" : "",
      currentInitials: isWeightLoss ? "WL" : isUrgentCare ? "UC" : ""
    };
  }, [pathname]);

  // Memoize active item calculation
  const activeItemUrl = useMemo(() => 
    getActiveItem(platformInfo.currentItems, pathname, program),
    [platformInfo.currentItems, pathname, program]
  );

  // Memoize callbacks
  const handleLogout = useCallback(() => {
    logout();
    setIsTopDropdownOpen(false);
  }, [logout]);

  const toggleMenu = useCallback(() => setMenuOpen(prev => !prev), []);

  // Early return if no items
  if (platformInfo.currentItems.length === 0) {
    return null;
  }

  return (
    <header
      className={cn(
        "shimmer-header sticky top-0 z-50 flex w-full shrink-0 gap-8 text-[#E9E9E9]/60 bg-[#0A1A2F] transition-[width,height] ease-linear sm:items-center px-4 justify-between border border-[#0A1A2F] sm:px-[60px] py-[24px]",
        platformInfo.isUrgentCare && "bg-[#2F0F04] border-[#2F0F04]"
      )}
      role="header"
    >
      <div className="flex items-center justify-center">
        <PlatformSwitcher
          currentTitle={platformInfo.currentTitle}
          currentInitials={platformInfo.currentInitials}
          user={user}
          isTopDropdownOpen={isTopDropdownOpen}
          setIsTopDropdownOpen={setIsTopDropdownOpen}
          pathname={pathname}
          isUrgentCarePlatform={platformInfo.isUrgentCare}
          handleLogout={handleLogout}
        />
      </div>

      <button
        className="md:hidden self-end cursor-pointer flex rounded-full border border-[#334155] p-2"
        onClick={toggleMenu}
      >
        <AlignRight className="h-5 w-7 text-white" />
      </button>

      {menuOpen && (
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent 
            title="header-menu" 
            side="left" 
            className={cn(
              "w-4/5 md:hidden bg-[#0A1A2F] border-0 text-white",
              platformInfo.isUrgentCare && "bg-[#2F0F04] border-[#2F0F04]"
            )}
          >
            <div className="mt-4 flex flex-col gap-4">
              <Link to="/dashboard" className="flex items-center gap-2">
                <LogoWhite className="h-8 w-auto" />
              </Link>
              {platformInfo.currentItems.map((item) => {
                const itemUrl = item.dynamicUrl ? item.dynamicUrl(program) : item.url;
                const isActive = itemUrl === activeItemUrl;
                return (
                  <Link
                    key={item.title}
                    to={itemUrl}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      "inline-flex items-center text-base space-y-2",
                      isActive ? "text-[#E9E9E9]" : "text-[#E9E9E9]"
                    )}
                  >
                    {isActive && (
                      <div className="mr-2 size-2 rounded-full bg-[#E9E9E9]" />
                    )}
                    {item.title}
                  </Link>
                );
              })}
              <div className="flex items-center justify-start gap-2">
                <Button variant="ghost" size="sm" className="flex items-center bg-[#FDFDFD0D] text-sm text-white">
                  <BellIcon className="size-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center bg-[#FDFDFD0D] text-sm text-white">
                      <UserIcon className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={logout}>
                      <LogOutIcon className="size-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      )}

      <DesktopNav 
        currentItems={platformInfo.currentItems}
        activeItemUrl={activeItemUrl}
        program={program}
      />

      <div className="md:flex hidden items-center justify-end gap-2 px-4">
        <Button variant="ghost" size="sm" className="flex items-center bg-[#FDFDFD0D] text-sm text-white">
          <BellIcon className="size-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="flex items-center bg-[#FDFDFD0D] text-sm text-white">
              <UserIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={logout}>
              <LogOutIcon className="size-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}